import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Divider, Drawer, Form, Input, Modal, Popover, Row, Segmented, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from "antd";
import WorkflowGraphEditor from "../Workflows/GraphEditor/WorkflowGraphEditor";
import {
  listModules, listWorkflowDefinitions, getWorkflowDefinition, createWorkflowDefinition, updateWorkflowDefinition, deleteWorkflowDefinition,
  getFieldOptions,
} from "../../apiservice/workflow";
import {
  listTaskTemplates, createTaskTemplate, updateTaskTemplate, deleteTaskTemplate,
} from "../../apiservice/workflow";
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

// An Aggregate condition with no "Sum this field" selected fails closed at
// evaluation time (backend/src/workflow/actions.js's validColumn check) —
// the condition silently always evaluates to false, so the workflow always
// takes the "No" branch. Block saving before that trap can happen.
function findEmptyAggregateConditionLabels(graph) {
  const labels = [];
  for (const node of graph?.nodes || []) {
    if (node.type !== "condition") continue;
    const conditions = node.data?.conditions;
    const list = Array.isArray(conditions?.conditions) ? conditions.conditions : [];
    const hasEmptyAggregate = list.some((c) => c?.type === "aggregate" && !c.aggregateField);
    if (hasEmptyAggregate) labels.push(node.data?.label || node.id);
  }
  return labels;
}

function WorkflowHelpContent() {
  return (
    <div style={{ maxWidth: 420 }}>
      <Paragraph strong style={{ marginBottom: 8 }}>Workflow help</Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 10 }}>
        A workflow starts with a <b>Start</b> node, moves through optional <b>Condition</b> nodes, and then runs one or more <b>Action</b> nodes.
      </Paragraph>
      <Paragraph style={{ marginBottom: 8 }}>
        <b>How to create one</b>
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 10 }}>
        Choose the module, set the trigger event, optionally add a match filter, then build the graph in Visual mode by connecting Start -> Condition/Action -> next step.
      </Paragraph>
      <Paragraph style={{ marginBottom: 8 }}>
        <b>Common actions</b>
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 10 }}>
        <b>Assign to User / Role</b>: pauses the flow until someone approves or rejects. <b>Assign Task</b>: opens a task form with checklist and file upload. <b>Update Field</b>: changes a record field. <b>Send Email / Notification</b>: informs users.
      </Paragraph>
      <Paragraph style={{ marginBottom: 8 }}>
        <b>Where tasks appear</b>
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 10 }}>
        Pending approval and task-assignment steps appear in <b>My tasks</b> at <Text code>/workflow-tasks</Text> and in the right sidebar under <b>Workflow Tasks</b>.
      </Paragraph>
      <Paragraph style={{ marginBottom: 8 }}>
        <b>Examples</b>
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 6 }}>
        <b>Sales approval:</b> On create -> if subtotal &gt;= 10000 -> assign to manager -> after approval -> update status to approved.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 6 }}>
        <b>Document collection:</b> On create -> assign task -> checklist: verify invoice, verify ID -> require file upload -> complete task -> notify finance.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        <b>Field names:</b> common variants such as <b>subtotal</b> and <b>sub_total</b> are matched leniently.
      </Paragraph>
    </div>
  );
}

const TRIGGER_EVENTS = [
  { value: "manual", label: "Manual only (no auto-fire)" },
  { value: "on_create", label: "On record create" },
  { value: "on_update", label: "On record update" },
  { value: "on_delete", label: "On record delete" },
  { value: "on_status_change", label: "On status change (backend support not yet implemented)", disabled: true },
];

const DEFAULT_GRAPH = {
  nodes: [
    { id: "n_start", type: "start", data: { label: "Start" } },
    {
      id: "n_approve",
      type: "action",
      data: {
        label: "Wait for approval",
        actionType: "Assign to User",
        target: 1, // user id placeholder
        dueInHours: 48,
      },
    },
    { id: "n_email", type: "action", data: { label: "Notify creator", actionType: "Send Email", to: "{{email}}", subject: "Your request was approved" } },
  ],
  edges: [
    { id: "e1", source: "n_start", target: "n_approve" },
    { id: "e2", source: "n_approve", target: "n_email" },
  ],
};

const SAMPLE_SALES_WORKFLOW = {
  name: "Sales subtotal ≥ 100 → approve",
  module: "sales",
  record_type: "sales",
  trigger_event: "on_update",
  priority: 100,
  match_filter: '[{"field":"subtotal","operator":">=","value":100}]',
  status: "Active",
  description: "When a sales invoice subtotal is at least 100, set status to approved.",
  graph: {
    nodes: [
      { id: "n_start", type: "start", data: { label: "Start" } },
      {
        id: "n_condition",
        type: "condition",
        data: {
          label: "Check subtotal",
          conditions: [{ field: "subtotal", operator: ">=", value: 100 }],
        },
      },
      {
        id: "n_action",
        type: "action",
        data: {
          label: "Approve invoice",
          actionType: "Update Field",
          updateField: "status",
          updateValue: "approved",
        },
      },
    ],
    edges: [
      { id: "e_start_condition", source: "n_start", target: "n_condition" },
      { id: "e_condition_action", source: "n_condition", target: "n_action", sourceHandle: "yes" },
    ],
  },
};


export default function WorkflowAdmin({ embedded = false }) {
  return (
    <div style={{ padding: embedded ? 0 : 16, maxWidth: 1200 }}>
      {!embedded && (
        <>
          <Space align="center" size={8} style={{ marginBottom: 4 }}>
            <Title level={3} style={{ marginTop: 0, marginBottom: 0 }}>Workflow automation</Title>
            <Popover content={<WorkflowHelpContent />} trigger="click" placement="rightTop">
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined style={{ color: "#1677ff" }} />}
                aria-label="Workflow help"
              />
            </Popover>
          </Space>
          <Paragraph type="secondary">
            Define reusable workflows and attach them to modules. A workflow fires based on its own module/event, priority, and optional match filter.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginTop: -6 }}>
            Info: Steps using <b>Assign Task</b>, <b>Assign to User</b>, or <b>Assign to Role</b> appear in <b>My tasks</b> (<Text code>/workflow-tasks</Text>) and in the right sidebar under <b>Workflow Tasks</b>.
          </Paragraph>
        </>
      )}
      <Tabs
        items={[
          { key: "defs", label: "Definitions", children: <DefinitionsTab /> },
          { key: "templates", label: "Task Templates", children: <TaskTemplatesTab /> },
        ]}
      />
    </div>
  );
}

function DefinitionsTab() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([listWorkflowDefinitions(), listModules()]);
      setRows(d?.data?.data || d?.data || []);
      setModules(m?.data?.data || m?.data || []);
    } catch (e) {
      message.error(e.message || "Load failed");
    } finally { setLoading(false); }
  }

  async function loadDefinitionForEdit(row) {
    if (!row?.id) {
      setEditing(row);
      setIsDrawerOpen(true);
      return;
    }
    if (editLoading) return; // Prevent multiple concurrent requests
    setEditLoading(true);
    try {
      const res = await getWorkflowDefinition(row.id);
      const payload = res?.data?.data || res?.data || row;
      if (!payload.graph) payload.graph = DEFAULT_GRAPH;
      setEditing(payload);
      setIsDrawerOpen(true);
    } catch (e) {
      message.error(e.message || "Failed to load workflow");
      setEditing(row);
      setIsDrawerOpen(true);
    } finally {
      setEditLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function onDelete(id) {
    Modal.confirm({
      title: "Delete workflow?",
      content: "Deleting a workflow removes its configured module/event firing rules.",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteWorkflowDefinition(id);
          message.success("Deleted");
          refresh();
        } catch (e) { message.error(e.message || "Delete failed"); }
      },
    });
  }

  return (
    <Card
      title="Workflow definitions"
      extra={(
        <Space>
          <Button type="default" onClick={() => { setEditing({ ...SAMPLE_SALES_WORKFLOW }); setIsDrawerOpen(true); }}>
            Use sample workflow
          </Button>
          <Button type="primary" onClick={() => { setEditing({ name: "", record_type: "", module: "", graph: DEFAULT_GRAPH, status: "Active", trigger_event: "manual", priority: 100, match_filter: "" }); setIsDrawerOpen(true); }}>
            New workflow
          </Button>
        </Space>
      )}
      loading={loading}
    >
      <Table
        rowKey="id"
        dataSource={rows}
        pagination={{ pageSize: 10 }}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Module / record_type", render: (_, r) => <Text code>{r.module || r.record_type}</Text> },
          { title: "Trigger", dataIndex: "trigger_event", render: (v) => <Tag>{v}</Tag>, width: 140 },
          { title: "Last triggered", dataIndex: "last_triggered_at", width: 180, render: (value) => value ? new Date(value).toLocaleString() : <Text type="secondary">Never</Text> },
          { title: "Priority", dataIndex: "priority", width: 90 },
          { title: "Status", dataIndex: "status", render: (s) => s === "Active" ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>, width: 100 },
          {
            title: "Actions", width: 180,
            render: (_, r) => (
              <Space>
                <Button size="small" onClick={() => loadDefinitionForEdit(r)} disabled={editLoading}>Edit</Button>
                <Button size="small" danger onClick={() => onDelete(r.id)}>Delete</Button>
              </Space>
            ),
          },
        ]}
      />
      {editing && (
        <DefinitionDrawer
          row={editing}
          modules={modules}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setTimeout(() => setEditing(null), 300);
          }}
          onSaved={() => {
            setIsDrawerOpen(false);
            setTimeout(() => { setEditing(null); refresh(); }, 300);
          }}
        />
      )}
    </Card>
  );
}

function DefinitionDrawer({ row, modules, isOpen, setIsOpen, onClose, onSaved }) {
  const [form] = Form.useForm();
  const triggerEvent = Form.useWatch("trigger_event", form);
  const [graph, setGraph] = useState(row.graph || DEFAULT_GRAPH);
  const [mode, setMode] = useState("Visual"); // Visual | JSON
  const [jsonText, setJsonText] = useState(JSON.stringify(row.graph || DEFAULT_GRAPH, null, 2));
  const [selectedModule, setSelectedModule] = useState(row.module || row.record_type || "");
  const [saving, setSaving] = useState(false);
  const [conditionFieldOptions, setConditionFieldOptions] = useState({ fields: [], relatedModules: [], lookups: {} });

  // Field/type metadata for the Condition Node's structured field picker.
  // Also feeds the Action node's "Update Field" picker (same real,
  // DB-verified field list — see fieldRegistry.js — rather than a second,
  // separately-maintained list). `lookups` resolves any "lookup"-typed
  // field (e.g. crm_lead's source_id) to real {value,label} options from
  // its master table, so the condition builder can show names instead of
  // raw UUIDs.
  useEffect(() => {
    if (!isOpen || !selectedModule) {
      setConditionFieldOptions({ fields: [], relatedModules: [], lookups: {} });
      return;
    }
    let cancelled = false;
    getFieldOptions(selectedModule)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        setConditionFieldOptions({ fields: data.fields || [], relatedModules: data.relatedModules || [], lookups: data.lookups || {} });
      })
      .catch(() => { if (!cancelled) setConditionFieldOptions({ fields: [], relatedModules: [], lookups: {} }); });
    return () => { cancelled = true; };
  }, [isOpen, selectedModule]);

  const anchorModule = selectedModule
    ? { key: selectedModule, label: modules.find((m) => m.key === selectedModule)?.label || selectedModule }
    : null;
  const relatedModules = conditionFieldOptions.relatedModules.map((r) => ({
    key: r.to,
    label: modules.find((m) => m.key === r.to)?.label || r.to,
    // carried through for the Aggregate condition type's auto-derived
    // match field (see WorkflowGraphEditor's PropertyPanel) — not used by
    // the Cascader picker itself.
    fkField: r.fkField,
  }));
  const fieldsByModule = anchorModule ? { [anchorModule.key]: conditionFieldOptions.fields } : {};

  useEffect(() => {
    if (!isOpen) return; // Only populate form when drawer is opening
    form.setFieldsValue({
      name: row.name || "",
      module: row.module || "",
      record_type: row.record_type || row.module || "",
      description: row.description || "",
      trigger_event: row.trigger_event || "manual",
      priority: row.priority ?? 100,
      match_filter: row.match_filter ? (typeof row.match_filter === "string" ? row.match_filter : JSON.stringify(row.match_filter, null, 2)) : "",
      allow_retrigger: !!row.allow_retrigger,
      status: row.status || "Active",
    });
    setSelectedModule(row.module || row.record_type || "");
    setGraph(row.graph || DEFAULT_GRAPH);
    setJsonText(JSON.stringify(row.graph || DEFAULT_GRAPH, null, 2));
  }, [row, form, isOpen]);

  // Switch between the visual builder and raw JSON, keeping them in sync.
  function switchMode(next) {
    if (next === mode) return;
    if (next === "JSON") {
      setJsonText(JSON.stringify(graph, null, 2));
    } else {
      try {
        setGraph(JSON.parse(jsonText));
      } catch (e) {
        message.error(`Invalid graph JSON: ${e.message}`);
        return;
      }
    }
    setMode(next);
  }

  async function onSave(values) {
    let nextGraph = graph;
    if (mode === "JSON") {
      try { nextGraph = JSON.parse(jsonText); }
      catch (e) { message.error(`Invalid graph JSON: ${e.message}`); return; }
    }
    if (!nextGraph || !Array.isArray(nextGraph.nodes) || !nextGraph.nodes.find((n) => n.type === "start")) {
      message.error("The workflow must have a Start node.");
      return;
    }
    const emptyAggregateLabels = findEmptyAggregateConditionLabels(nextGraph);
    if (emptyAggregateLabels.length > 0) {
      message.error(`Select "Sum this field" for the Aggregate condition in: ${emptyAggregateLabels.join(", ")}`);
      return;
    }
    const graphToSave = nextGraph;
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        record_type: values.record_type || values.module,
        module: values.module,
        description: values.description,
        trigger_event: values.trigger_event,
        priority: Number(values.priority) || 100,
        match_filter: values.match_filter || null,
        allow_retrigger: values.trigger_event === "on_update" ? !!values.allow_retrigger : false,
        status: values.status,
        graph: graphToSave,
      };
      if (row.id) await updateWorkflowDefinition(row.id, payload);
      else await createWorkflowDefinition(payload);
      message.success("Saved");
      onSaved();
    } catch (e) {
      message.error(e.message || "Save failed");
    } finally { setSaving(false); }
  }

  const handleCloseDrawer = () => {
    setIsOpen(false);
    setTimeout(() => onClose(), 300);
  };

  return (
    <Drawer
      title={row.id ? `Edit workflow "${row.name || row.id}"` : "New workflow"}
      open={isOpen}
      onClose={handleCloseDrawer}
      width={Math.min(1200, typeof window !== "undefined" ? window.innerWidth - 40 : 1200)}
      styles={{ body: { padding: "24px 32px" } }}
    >
      <Form form={form} layout="vertical" onFinish={onSave} requiredMark="optional">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Quote > $10k needs approval" />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="module" label="Module" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select module"
                options={modules.map((m) => ({ value: m.key, label: `${m.label}  (${m.key})` }))}
                onChange={(v) => {
                  form.setFieldsValue({ record_type: v });
                  setSelectedModule(v);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="record_type" label="record_type (table key)" rules={[{ required: true }]}>
              <Input placeholder="defaults to module key" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="trigger_event" label="Trigger event" rules={[{ required: true }]}>
              <Select options={TRIGGER_EVENTS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="priority" label="Priority" help="Lower numbers run first" rules={[{ required: true }]}> 
              <Input type="number" />
            </Form.Item>
          </Col>
        </Row>
        {/* Only shown for on_update: an update-triggered workflow can otherwise
            start a fresh (no-op) instance on every unrelated edit to a record,
            which both spams "workflow triggered" notifications and — for a
            workflow with allow_retrigger on — leaves orphaned Created tasks on
            the branch the condition didn't take, permanently blocking future
            real triggers. Filtering here, before any task rows are created,
            avoids both. Hidden for other trigger events, where it doesn't apply. */}
        {triggerEvent === "on_update" && (
          <Form.Item name="match_filter" label="Match filter (JSON)" extra='Optional filter — skip starting the workflow at all unless the record matches this condition list.'>
            <Input.TextArea rows={3} placeholder='[{"field":"total_amount","operator":">","value":10000}]' style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }} />
          </Form.Item>
        )}
        {triggerEvent === "on_update" && (
          <Form.Item name="allow_retrigger" valuePropName="checked" extra="By default a workflow runs at most once per record, ever. Enable this to let it run again on a later edit, once the prior run has fully resolved (approved/rejected).">
            <Checkbox>Allow re-approval on later edits</Checkbox>
          </Form.Item>
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="status" label="Status" hidden>
              <Select options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label="Description" hidden>
          <Input.TextArea rows={2} placeholder="Internal note for your team" />
        </Form.Item>
        <Form.Item
          label={
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <span>Workflow steps</span>
              <Segmented size="small" value={mode} onChange={switchMode} options={["Visual", "JSON"]} />
            </Space>
          }
          extra={mode === "Visual"
            ? "Add Condition / Action steps, drag from a node's bottom dot to another's top to connect. Condition nodes have Yes/No outputs. Click a node to edit it. Action types include Assign Task."
            : "nodes[] (with one start) + edges[]. Action types: Send Email, Send Notification, Assign to User, Assign to Role, Update Field, Assign Task. Note: field names are matched leniently (for example subtotal and sub_total)."}
        >
          {mode === "Visual" ? (
            <WorkflowGraphEditor
              value={graph}
              onChange={setGraph}
              fieldOptions={conditionFieldOptions.fields.map((f) => f.name)}
              anchorModule={anchorModule}
              relatedModules={relatedModules}
              fieldsByModule={fieldsByModule}
              modules={modules}
              lookups={conditionFieldOptions.lookups}
            />
          ) : (
            <Input.TextArea
              rows={16}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
            />
          )}
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Drawer>
  );
}

// ─── Task Templates ──────────────────────────────────────────────────────────

function TaskTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await listTaskTemplates();
      setRows(res?.data?.data || res?.data || []);
    } catch (e) {
      message.error(e.message || "Failed to load task templates");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  function onDelete(id) {
    Modal.confirm({
      title: "Delete task template?",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteTaskTemplate(id);
          message.success("Deleted");
          refresh();
        } catch (e) { message.error(e.message || "Delete failed"); }
      },
    });
  }

  return (
    <Card
      title="Task templates"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setEditing({ name: "", description: "", checklist: [], allow_file_upload: false, file_upload_required: false })}
        >
          New template
        </Button>
      }
      loading={loading}
    >
      <Typography.Paragraph type="secondary">
        Define reusable task templates with a checklist and optional file upload requirement.
        Reference a template when building a workflow step with the <b>Assign Task</b> action.
      </Typography.Paragraph>
      <Table
        rowKey="id"
        dataSource={rows}
        pagination={{ pageSize: 10 }}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Description", dataIndex: "description", render: (v) => v || <Text type="secondary">—</Text> },
          {
            title: "Checklist",
            dataIndex: "checklist",
            render: (list) => {
              const items = Array.isArray(list) ? list : [];
              if (!items.length) return <Text type="secondary">No items</Text>;
              return (
                <Space wrap>
                  {items.map((c, i) => (
                    <Tag key={i} color={c.required ? "orange" : "default"}>
                      {c.label || `Step ${i + 1}`}{c.required ? " *" : ""}
                    </Tag>
                  ))}
                </Space>
              );
            },
          },
          {
            title: "File upload",
            render: (_, r) =>
              r.allow_file_upload
                ? <Tag color={r.file_upload_required ? "red" : "blue"}>{r.file_upload_required ? "Required" : "Optional"}</Tag>
                : <Text type="secondary">—</Text>,
            width: 120,
          },
          {
            title: "Actions",
            width: 140,
            render: (_, r) => (
              <Space>
                <Button size="small" onClick={() => setEditing(r)}>Edit</Button>
                <Button size="small" danger onClick={() => onDelete(r.id)}>Delete</Button>
              </Space>
            ),
          },
        ]}
      />
      {editing && (
        <TemplateDrawer
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </Card>
  );
}

function TemplateDrawer({ row, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [checklist, setChecklist] = useState(Array.isArray(row.checklist) ? row.checklist : []);
  const [allowFile, setAllowFile] = useState(Boolean(row.allow_file_upload));
  const [fileRequired, setFileRequired] = useState(Boolean(row.file_upload_required));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ name: row.name || "", description: row.description || "" });
    setChecklist(Array.isArray(row.checklist) ? row.checklist : []);
    setAllowFile(Boolean(row.allow_file_upload));
    setFileRequired(Boolean(row.file_upload_required));
  }, [row, form]);

  async function onSave(values) {
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        description: values.description || null,
        checklist,
        allow_file_upload: allowFile,
        file_upload_required: allowFile && fileRequired,
      };
      if (row.id) await updateTaskTemplate(row.id, payload);
      else await createTaskTemplate(payload);
      message.success("Saved");
      onSaved();
    } catch (e) {
      message.error(e.message || "Save failed");
    } finally { setSaving(false); }
  }

  function addItem() {
    setChecklist((prev) => [...prev, { id: `c_${Date.now()}`, label: "", required: false }]);
  }

  function updateItem(idx, patch) {
    setChecklist((prev) => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  }

  function removeItem(idx) {
    setChecklist((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <Drawer
      title={row.id ? `Edit template "${row.name}"` : "New task template"}
      open
      onClose={onClose}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onSave} requiredMark="optional">
        <Form.Item name="name" label="Template name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Document review checklist" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Brief description of this task template" />
        </Form.Item>

        <Divider orientation="left">Checklist items</Divider>
        {checklist.map((item, idx) => (
          <div key={item.id || idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <Input
              style={{ flex: 1 }}
              value={item.label}
              placeholder={`Step ${idx + 1}`}
              onChange={(e) => updateItem(idx, { label: e.target.value })}
            />
            <Checkbox
              checked={item.required}
              onChange={(e) => updateItem(idx, { required: e.target.checked })}
            >
              Required
            </Checkbox>
            <Button
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => removeItem(idx)}
            />
          </div>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={addItem}
          style={{ marginBottom: 16 }}
        >
          Add checklist item
        </Button>

        <Divider orientation="left">File upload</Divider>
        <Form.Item label="Allow file upload">
          <Switch
            checked={allowFile}
            onChange={(v) => { setAllowFile(v); if (!v) setFileRequired(false); }}
          />
        </Form.Item>
        {allowFile && (
          <Form.Item label="File upload required">
            <Switch checked={fileRequired} onChange={setFileRequired} />
          </Form.Item>
        )}

        <Space style={{ marginTop: 8 }}>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Drawer>
  );
}


